import React, {FunctionComponent, useEffect, useState} from "react"
import {useCustomer} from "../../common/contexts/CustomerContext"
import Button from "@murphy-frontend/web-core/components/Button"
import {useDroneEvents, useStartedDroneEvents} from "@murphy-frontend/common/api/DroneEvents/Queries"
import {useCompleteEvent, useStartEvent, useReopenEvent} from "@murphy-frontend/common/api/DroneEvents/Mutations"
import StartedEventFlowsTable from "./components/StartedEventFlowsTable"
import {MurphyDroneEventDto} from "@murphy-frontend/common/interfaces/IDroneEventsApi"
import useWindowDimensions from "@murphy-frontend/web-core/hooks/useWindowDimensions"
import {useNavigate} from "react-router-dom"
import {useUserPreferences} from "../../common/contexts/UserPreferencesContext"
import FilterControl, {FilterType} from "@murphy-frontend/web-core/components/FilterControl"
import SearchBar from "@murphy-frontend/web-core/components/SearchBar"
import {StartEventDto} from "@murphy-frontend/common/api/DroneEvents/models"
import TabPanel, {TabType} from "@murphy-frontend/web-core/components/Tabs/TabPanel"
import ComponentWrapper from "../../common/components/ComponentWrapper"
import EventTemplates from "./components/EventTemplates"

interface EventFlowsProps {}

const EventFlows: FunctionComponent<EventFlowsProps> = () => {
	const {customer} = useCustomer()
	const {data: startedEvents, isLoading: startedEventsIsLoading} = useStartedDroneEvents(customer?.Id, true)
	const {data: eventTemplates, isLoading: eventTemplatesIsLoading} = useDroneEvents(customer?.Id)
	const {mutate: startEvent, isPending: startEventIsPending} = useStartEvent(customer?.Id)
	const {mutate: compeleteEvent, isPending: completeEventIsPending} = useCompleteEvent(customer?.Id)
	const {mutate: reopenEvent, isPending: reopenEventIsPending} = useReopenEvent(customer?.Id)

	const [filters, setFilters] = useState<FilterType[]>([])
	const [markAllIsToggled, setMarkAllIsToggled] = useState<boolean>(true)

	const [filteredStartedEvents, setFilteredStartedEvents] = useState([])
	const [currentSearchText, setCurrentSearchText] = useState("")
	const [selectedTabId, setSelectedTabId] = useState<number>(0)

	const {timeZone, translations} = useUserPreferences()

	const {currentSize} = useWindowDimensions()
	const navigate = useNavigate()

	const onClickStartedEvent = (dto: MurphyDroneEventDto) => {
		const url = `/startedeventflows/${dto.Id}`
		navigate(url)
	}

	const initialTabs: TabType[] = [
		{
			id: 0,
			displayText: translations.newIncidents,
			customClass: "tab-transparent-panel-tab",
			customSelectedClass: "tab-transparent-default-panel-tab-selected wrap-text"
		},
		{
			id: 1,
			displayText: translations.allIncidents,
			customClass: "tab-transparent-panel-tab",
			customSelectedClass: "tab-transparent-default-panel-tab-selected wrap-text"
		},
		{
			id: 2,
			displayText: translations.activeIncidents,
			customClass: "tab-transparent-panel-tab",
			customSelectedClass: "tab-transparent-default-panel-tab-selected wrap-text"
		},
		{
			id: 3,
			displayText: translations.closedIncidents,
			customClass: "tab-transparent-panel-tab",
			customSelectedClass: "tab-transparent-default-panel-tab-selected wrap-text"
		}
	]

	const onClickTab = (tabId: number) => {
		setSelectedTabId(tabId)
	}

	const onClickNew = () => {
		// get last event template
		const eventTemplateId = eventTemplates?.[0]?.Id

		const startEventDto: StartEventDto = {
			EventFlowTemplateId: eventTemplateId,
			GeoLocation: {
				GeoX: "13.049378067221362",
				GeoY: "55.5808510100517",
				DeniedGeo: false
			}
		}

		startEvent(startEventDto)
		// navigate('/startedeventflows/new');
	}

	const onClickCloseStartedEvent = (dto: MurphyDroneEventDto) => {
		compeleteEvent(dto.Id)
	}

	const onClickReopenClosedEvent = (dto: MurphyDroneEventDto) => {
		reopenEvent(dto.Id)
	}

	const onClickLocation = (dto: MurphyDroneEventDto) => {
		window.open("https://maps.google.com?q=" + dto.Latitude + "," + dto.Longitude)
	}

	const onToggleMarkAllFilters = () => {
		const newAllRows = filters.map(p => {
			return {...p, isActive: !markAllIsToggled}
		})

		setFilters(newAllRows)
		setMarkAllIsToggled(!markAllIsToggled)
	}

	const onToggleFilter = (filt: FilterType) => {
		const updatedFilters = filters.map(filter => {
			const updatedItem = {...filter}
			if (filter.columnname === filt.columnname && filter.filtervalue === filt.filtervalue) {
				updatedItem.isActive = !filter.isActive
			}
			return updatedItem
		})

		if (updatedFilters.every(p => p.isActive)) {
			setMarkAllIsToggled(true)
		} else {
			setMarkAllIsToggled(false)
		}
		setFilters(updatedFilters)
	}

	const onResetFilters = () => {
		const initFilters = generateInitialFilters()
		setFilters(initFilters)
	}

	const generateInitialFilters = (): Array<FilterType> => {
		const allPossibleFilters = [
			{
				displayName: "Open",
				columnname: "Open",
				filtervalue: true,
				isActive: true
			},
			{
				displayName: "Closed",
				columnname: "Open",
				filtervalue: false,
				isActive: false
			}
		]
		return allPossibleFilters
	}

	const onSearchChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
		const newValue = event.target.value
		setCurrentSearchText(newValue)
	}

	useEffect(() => {
		if (translations) {
			const initFilters = generateInitialFilters()
			setFilters(initFilters)
		}
	}, [translations])

	useEffect(() => {
		if (!startedEvents) {
			return
		}

		let newFilteredList = startedEvents

		//for (let i = 0; i < filters.length; i++) {
		//	const currentFilter = filters[i]
		//	if (currentFilter.isActive === true) {
		//		const currentFilterMatches = startedEvents.filter((recipient: any) => {
		//			if (recipient[currentFilter.columnname] === currentFilter.filtervalue) {
		//				return recipient
		//			}
		//		})
		//		newFilteredList = newFilteredList.concat(currentFilterMatches)
		//	}
		//}

		if (currentSearchText) {
			const searchValue = currentSearchText.toUpperCase()
			newFilteredList = newFilteredList.filter(
				p =>
					p.Name.toUpperCase().startsWith(searchValue) ||
					p.CreatedByUserEmail.toUpperCase().startsWith(searchValue)
			)
		}

		switch (selectedTabId) {
			case 2:
				newFilteredList = newFilteredList.filter(ev => ev.Open)
				break
			case 3:
				newFilteredList = newFilteredList.filter(ev => !ev.Open)
				break
			default:
			// do nothing
		}
		setFilteredStartedEvents(newFilteredList)
	}, [startedEvents, filters, currentSearchText, selectedTabId])

	return (
		<ComponentWrapper active={6}>
			<section className='header-btns'>
				<h2>{translations.eventflowsmodulename}</h2>
			</section>
			<section className='user-tab-wrapper'>
				<TabPanel
					tabs={initialTabs}
					onClickTab={onClickTab}
					selectedTabId={selectedTabId}
					customClass='tab-solid-panel-tms'
				/>
			</section>
			{selectedTabId === 0 ? (
				<EventTemplates />
			) : (
				<>
					<section className='search-wrapper'>
						<SearchBar
							val={currentSearchText}
							handleChange={onSearchChanged}
							customClasses={["search-bar-full-width"]}
							placeholder={translations.incidentsPlaceholder}
							showSearchIcon={true}
						/>
					</section>
					<StartedEventFlowsTable startedEventFlows={filteredStartedEvents ?? []} timeZone={timeZone} />
				</>
			)}
		</ComponentWrapper>
	)
}

export default EventFlows
